import { GoogleGenAI, Type } from "@google/genai";
import { ScenarioConfig, UserProfile, GeminiResponseSchema, ChatMessage } from "../types";

// Lazy initialization holder
let ai: GoogleGenAI | null = null;
let chatSession: any | null = null; // Use any to avoid type import issues at runtime

const getAIClient = () => {
  if (!ai) {
    // Access process.env.API_KEY safely
    const apiKey = process.env.API_KEY;
    
    if (!apiKey) {
      console.error("CRITICAL: API_KEY is missing in the environment.");
    } else {
      console.log(`Gemini Client Initialized.`);
    }
    
    // Initialize anyway to allow catching the error later if needed
    try {
        ai = new GoogleGenAI({ apiKey: apiKey || '' });
    } catch (e) {
        console.error("Failed to initialize GoogleGenAI client:", e);
    }
  }
  return ai;
};

// Helper to format history for Gemini
const formatHistory = (historyMessages: ChatMessage[]) => {
    return historyMessages
        .filter(msg => msg.text && msg.text.trim().length > 0) // Remove empty messages
        .filter(msg => msg.id !== 'init') // Remove the "Online" system-like status messages
        .map(msg => ({
            role: msg.sender === 'user' ? 'user' : 'model',
            parts: [{ text: msg.text }]
        }));
};

export const initializeChatSession = (
  user: UserProfile, 
  scenario: ScenarioConfig,
  historyMessages: ChatMessage[] = []
) => {
  // Construct system instruction without relying on onboardingGoal or onboardingStruggle
  // to ensure consistency with mobile app data structure.

  const systemInstruction = `
    You are a dual-mode AI engine for a relationship coaching app called "LoveOnRead".
    
    ROLE 1: THE PARTNER SIMULATOR
    You must roleplay as a ${user.targetGender} interacting with a ${user.gender} named ${user.name}.
    Context of the relationship: ${scenario.relationshipStatus}.
    Specific Scenario Details: ${scenario.context}.
    Difficulty Level: ${scenario.difficulty}.
    
    If Difficulty is Hard, be less responsive, use shorter answers, and require high-quality engagement to open up.
    If Difficulty is Easy, be more forgiving and talkative.

    ROLE 2: THE DATING COACH
    Simultaneously, you act as a world-class communication coach. You analyze the user's *last* message.
    Did they ask an open-ended question? Was it boring? Was it too needy? Was it charming?

    OUTPUT FORMAT:
    You must respond in strict JSON format.
    {
      "personaResponse": "The text response from the character you are roleplaying.",
      "coachingTip": "A short, actionable tip or critique about the user's message. Be direct. E.g., 'Too generic, try referencing her bio' or 'Great use of humor!'.",
      "score": 7 (An integer 1-10 rating the user's effectiveness)
    }
  `;

  // Safely convert ChatMessage[] to Gemini Content format
  let history: any[] = [];
  
  try {
      history = formatHistory(historyMessages);
  } catch (err) {
      console.warn("Failed to parse chat history:", err);
      history = []; // Fallback to empty history
  }

  // Initialize client here
  const client = getAIClient();
  
  if (!client) {
      console.error("Gemini client is not initialized.");
      return null;
  }
  
  try {
    chatSession = client.chats.create({
        model: 'gemini-3-flash-preview',
        config: {
        systemInstruction,
        responseMimeType: 'application/json',
        responseSchema: {
            type: Type.OBJECT,
            properties: {
            personaResponse: { type: Type.STRING },
            coachingTip: { type: Type.STRING },
            score: { type: Type.INTEGER }
            },
            required: ["personaResponse", "coachingTip", "score"]
        }
        },
        history: history
    });
  } catch (error) {
      console.error("Failed to create chat session:", error);
      // Fallback: Create session without history
      try {
        chatSession = client.chats.create({
            model: 'gemini-3-flash-preview',
            config: {
                systemInstruction,
                responseMimeType: 'application/json',
                responseSchema: {
                    type: Type.OBJECT,
                    properties: {
                        personaResponse: { type: Type.STRING },
                        coachingTip: { type: Type.STRING },
                        score: { type: Type.INTEGER }
                    },
                    required: ["personaResponse", "coachingTip", "score"]
                }
            }
        });
      } catch (retryError) {
          console.error("Critical failure creating chat session:", retryError);
      }
  }

  return chatSession;
};

export const sendMessageToGemini = async (userMessage: string): Promise<GeminiResponseSchema> => {
  if (!chatSession) {
    // Attempt to recover if possible, but usually this means init failed
    console.warn("Chat session null, attempting to throw friendly error.");
    throw new Error("Chat session not initialized. Please refresh the page.");
  }

  try {
    const response = await chatSession.sendMessage({ message: userMessage });
    
    let text = response.text;
    if (!text) throw new Error("Empty response from AI");

    // Clean potential markdown code blocks
    text = text.replace(/^```json\s*/, '').replace(/^```\s*/, '').replace(/\s*```$/, '');

    const parsed: GeminiResponseSchema = JSON.parse(text);
    return parsed;
  } catch (error: any) {
    console.error("Gemini Interaction Error:", error);
    
    let userErrorMessage = "Network error or AI format issue. Try sending your message again.";
    
    if (error.message) {
      if (error.message.includes('403') || error.message.includes('API key')) {
        userErrorMessage = "Configuration Error: API Key is missing or invalid.";
      } else if (error.message.includes('429')) {
        userErrorMessage = "You are chatting too fast. Please wait a moment.";
      } else if (error.message.includes('503')) {
        userErrorMessage = "The AI service is currently overloaded. Please try again later.";
      }
    }

    return {
      personaResponse: "...",
      coachingTip: userErrorMessage,
      score: 5
    };
  }
};